<script lang="ts">
  import type {
    ClientToServerEvent as OverlayClientToServerEvents,
    ServerToClientEvents as OverlayServerToClientEvents,
  } from '@pin/schemas/overlay';
  import type { UserBanInfo } from '@pin/schemas/rest';
  import type { ChatMessageAdmin } from '@pin/schemas/socket';
  import { format } from 'date-fns';
  import { nb } from 'date-fns/locale';
  import type { Socket } from 'socket.io-client';
  import { tick } from 'svelte';
  import { SvelteSet } from 'svelte/reactivity';
  import { fetchUserMessages, fetchUserSub, pollUserBanInfo } from '../api/api';
  import { addToast } from '../stores/toastStore';
  import type { ChatMessage } from '../utils/history';
  import { subLookups } from '../utils/subLookups';
  import { isUserMuted, userWithAccount } from '../utils/userState';
  import MessageActionButtons from './MessageActionButtons.svelte';
  import UserActionButtons from './UserActionButtons.svelte';

  interface Props {
    overlaySocket: Socket<
      OverlayServerToClientEvents,
      OverlayClientToServerEvents
    > | null;
    chatMessages: ChatMessage[];
    userFeedUser: ChatMessageAdmin['user'] | null;
  }
  let {
    overlaySocket,
    chatMessages,
    userFeedUser = $bindable(),
  }: Props = $props();

  let chatBox: HTMLUListElement | null = $state(null);
  let autoScroll = $state(true);

  let loadHistoryInProgress = $state(false);
  let messagesFromHistory = $state<ChatMessageAdmin[]>([]);
  let banInfo = $state<UserBanInfo | null>(null);
  // Keyed by the user they belong to, so switching users in the panel can
  // never show one user's sub under another's name.
  let subs = $state<{
    userId: string;
    rows: { label: string; sub: string | null }[];
  } | null>(null);
  let subsLoadingFor = $state<string | null>(null);
  let shownSubs = $derived(
    subs != null && subs.userId === userFeedUser?.id ? subs.rows : null
  );
  let subsLoading = $derived(
    subsLoadingFor != null && subsLoadingFor === userFeedUser?.id
  );
  let messagesToShow = $derived.by(() => {
    const seen = new SvelteSet<string>();
    const deduplicated: ChatMessageAdmin[] = [];
    for (const message of chatMessages) {
      if (message.user.id === userFeedUser?.id && !seen.has(message.id)) {
        seen.add(message.id);
        deduplicated.push(message);
      }
    }
    // History is not always cleared on a switch of user, and a load still
    // running can land after one
    for (const message of messagesFromHistory) {
      if (message.user.id === userFeedUser?.id && !seen.has(message.id)) {
        seen.add(message.id);
        deduplicated.push(message);
      }
    }
    deduplicated.sort((a, b) => a.ts - b.ts);
    if (autoScroll) {
      scrollToBottom();
    }
    return deduplicated;
  });

  let isAdminUser = $derived(messagesToShow.some((m) => m.role === 'admin'));
  let isMuted = $derived(
    userFeedUser == null ? false : isUserMuted(chatMessages, userFeedUser.id)
  );
  // A ban from the header reaches the account a child profile acts through,
  // as one from a message does
  const resolveActionUser = (user: ChatMessageAdmin['user']) =>
    userWithAccount(user, messagesToShow, (userId) =>
      fetchUserMessages(userId, { take: 1 })
    );

  const onLoadMoreUserHistory = async (take: number) => {
    const userId = userFeedUser?.id;
    if (userId == null) {
      return addToast({
        message: `Couldn't fetch user history`,
        type: 'error',
      });
    }
    const startId = messagesToShow.at(0)?.id;
    try {
      const history = await fetchUserMessages(userId, {
        startId,
        take,
      });
      if (history.length === 0) {
        addToast({ message: 'No more messages to fetch' });
      }
      messagesFromHistory = [...messagesFromHistory, ...history];
    } catch (e) {
      addToast({
        message: `Couldn't fetch user history: ${String(e)}`,
        type: 'error',
      });
    }
  };

  const onShowSubs = async () => {
    const user = userFeedUser;
    if (user == null) {
      return;
    }
    subsLoadingFor = user.id;
    try {
      // Opened from the overlay, the user has only id and name, and
      // their messages may have left the feed. Take the account link from
      // a message of theirs, so a child profile still gets its via_sub.
      const message =
        messagesToShow.at(-1) ??
        (await fetchUserMessages(user.id, { take: 1 })).at(0);
      const viaUserId = message?.user.viaUserId ?? user.viaUserId;
      const rows = await Promise.all(
        subLookups(user.id, viaUserId).map(async ({ label, userId }) => ({
          label,
          sub: await fetchUserSub(userId),
        }))
      );
      subs = { userId: user.id, rows };
    } catch (e) {
      addToast({ message: `Couldn't fetch sub: ${String(e)}`, type: 'error' });
    } finally {
      if (subsLoadingFor === user.id) {
        subsLoadingFor = null;
      }
    }
  };

  const copySub = async (sub: string) => {
    try {
      await navigator.clipboard.writeText(sub);
      addToast({ message: 'Sub copied' });
    } catch (e) {
      addToast({ message: `Couldn't copy: ${String(e)}`, type: 'error' });
    }
  };

  const scrollToBottom = async () => {
    await tick();
    if (chatBox != null) {
      chatBox.scrollTo(0, chatBox.scrollHeight);
    }
  };

  const handleAutoscroll = (e: Event) => {
    const target = e.target as HTMLDivElement;
    const marginOfError = 5;
    autoScroll =
      target.scrollHeight - target.scrollTop - target.clientHeight <=
      marginOfError;
  };

  $effect(() => {
    if (autoScroll && chatMessages.length) {
      scrollToBottom();
    }
  });

  let previousUserId = userFeedUser?.id;
  $effect(() => {
    const currentUserId = userFeedUser?.id;
    if (previousUserId !== currentUserId || currentUserId == null) {
      messagesFromHistory = [];
      banInfo = null;
    }
    previousUserId = currentUserId;
  });

  $effect(() => {
    const userId = userFeedUser?.id;
    if (userId == null) {
      return;
    }
    const sub = pollUserBanInfo(userId).subscribe({
      next: (info) => (banInfo = info),
      error: () => (banInfo = null),
    });
    return () => sub.unsubscribe();
  });
</script>

<div class="container">
  <div class="header-container">
    {#if userFeedUser == null}
      <h2 class="header">User Info</h2>
    {:else}
      <div class="user-panel-header">
        <h2 class:admin-user={isAdminUser}>{userFeedUser.name}</h2>
        {#if userFeedUser.birthYear != null}
          <span class="birthyear">({userFeedUser.birthYear})</span>
        {/if}
        {#if banInfo == null}
          <span class="spinner ban-info-spinner" aria-label="Loading ban info"
          ></span>
        {:else}
          <span class="ban-info">
            {banInfo.banCount}
            {banInfo.banCount === 1 ? 'ban' : 'bans'}
            {#if banInfo.currentBan != null}
              (<span class="ban-info-active-ban">
                {banInfo.currentBan.expiresAt != null
                  ? `banned until ${format(banInfo.currentBan.expiresAt, 'dd.MM HH:mm', { locale: nb })}`
                  : 'banned'}
              </span>)
            {/if}
          </span>
        {/if}
        <!-- Remounted for each user, so an open menu never acts on the next -->
        {#key userFeedUser.id}
          <UserActionButtons
            user={userFeedUser}
            isBanned={banInfo?.currentBan != null}
            {isMuted}
            placement="down"
            resolveUser={resolveActionUser}
          />
        {/key}
      </div>
    {/if}
    <button
      class="btn"
      onclick={() => (userFeedUser = null)}
      disabled={userFeedUser == null}>Close</button
    >
  </div>
  {#if userFeedUser != null && !isAdminUser}
    <!-- For giveaways: innlogging finds the winner's email from the sub. -->
    {#if shownSubs == null}
      <button
        class="btn"
        disabled={subsLoading}
        aria-busy={subsLoading}
        onclick={onShowSubs}
      >
        Show innlogging sub
        {#if subsLoading}
          <span class="spinner" aria-hidden="true"></span>
        {/if}
      </button>
    {:else}
      <dl class="subs">
        {#each shownSubs as { label, sub } (label)}
          <dt>{label}</dt>
          <dd>
            {#if sub == null}
              <span class="sub-unknown">Not connected in the last 90 days</span>
            {:else}
              <code>{sub}</code>
              <button class="btn" onclick={() => copySub(sub)}>Copy</button>
            {/if}
          </dd>
        {/each}
      </dl>
    {/if}
  {/if}
  {#if userFeedUser != null}
    <button
      class="btn thick"
      disabled={loadHistoryInProgress}
      aria-busy={loadHistoryInProgress}
      onclick={async () => {
        loadHistoryInProgress = true;
        await onLoadMoreUserHistory(100);
        loadHistoryInProgress = false;
      }}
    >
      Load history
      {#if loadHistoryInProgress}
        <span class="spinner" aria-hidden="true"></span>
      {/if}</button
    >
    <ul class="messages" bind:this={chatBox} onscroll={handleAutoscroll}>
      {#each messagesToShow as message (message.id)}
        <li class="message">
          {message.text}
          <span class="sent-time">
            Sent: {format(message.ts, 'dd.MM.yy HH:mm:ss', { locale: nb })}
          </span>
          <MessageActionButtons {message} {overlaySocket} />
        </li>
      {/each}
    </ul>
  {/if}
</div>

<style>
  .container {
    display: flex;
    flex-direction: column;
    height: 100%;
    padding: 0.5rem;
    gap: 0.5rem;
  }

  .header-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.25rem 0;
  }

  .header {
    font-size: 1.25rem;
    font-weight: 600;
    margin: 0;
    color: var(--color-zinc-200);
  }

  .user-panel-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
  }

  .admin-user {
    font-weight: bold;
    font-size: 0.9rem;
    padding: 0.2rem 0.3rem;
    color: black !important;
    background: #a3e635;
    border-radius: 5px;
    margin: 0.25rem 0;
  }

  h2 {
    margin: 0;
    font-size: 1.25rem;
    font-weight: 600;
    color: var(--color-zinc-200);
  }

  .birthyear {
    font-size: 0.9rem;
    color: var(--color-zinc-400);
  }

  .ban-info {
    font-size: 0.75rem;
    color: var(--color-zinc-400);
    align-self: center;
  }

  .ban-info-active-ban {
    color: #f87171;
  }

  .ban-info-spinner {
    width: 0.75rem;
    height: 0.75rem;
    align-self: center;
  }

  .subs {
    display: grid;
    grid-template-columns: max-content 1fr;
    gap: 0.25rem 0.5rem;
    align-items: center;
    margin: 0;
    font-size: 0.8rem;
  }
  .subs dt {
    color: var(--color-zinc-400);
  }
  .subs dd {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    margin: 0;
    min-width: 0;
  }
  .subs code {
    overflow-wrap: anywhere;
    user-select: all;
  }
  .subs .btn {
    font-size: 0.75rem;
  }
  .sub-unknown {
    color: var(--color-zinc-500);
  }

  .messages {
    flex: 1;
    list-style: none;
    padding: 0;
    overflow-y: auto;
    margin: 0.5rem 0;
  }
  .message {
    display: flex;
    flex-direction: column;
    word-break: break-word;
    margin-bottom: 10px;
    border-bottom: 1px solid #333;
    padding-bottom: 6px;
  }

  .btn {
    display: flex;
    align-self: center;
    gap: 0.25rem;
    padding: 0.25rem 0.5rem;
    background: var(--color-zinc-700);
    color: var(--color-zinc-200);
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 1rem;
    line-height: 1;
    transition: background 0.2s ease;
    width: max-content;
  }
  .btn:hover {
    background: var(--color-zinc-600);
  }

  .btn:focus {
    outline: none;
    border-color: var(--color-zinc-400);
    border-width: 1px;
    border-style: solid;
    border-radius: 0.5rem;
  }
  .btn:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }

  .btn.thick {
    padding: 0.5rem 1rem;
    border-radius: 0.5rem;
  }

  .sent-time {
    font-size: 0.7rem;
    color: var(--color-zinc-500);
    user-select: none;
  }
</style>
